import { FormEvent, ReactNode, useState } from "react";
import { Helmet } from "react-helmet-async";
import logoFull from "@/assets/logo-full-white-transparent.png";

export type LeadMagnetImage = {
  src: string;
  alt: string;
  width?: number;
  height?: number;
};

export type LeadMagnetConfig = {
  path: string;
  tag: "lm-appointment-setting" | "lm-posting" | "lm-video-ad";
  /** Which lead magnet the submit function logs and sends to its own webhook. */
  leadMagnet: "appointment-setting" | "posting" | "video-ad";
  /** Optional small link shown under the form. */
  footerLink?: { href: string; label: string };
  eyebrow: string;
  headline: string;
  subtitle: string;
  cover?: string;
  coverAlt?: string;
  /** Preview shown instead of document pictures (e.g. a built cover card while screenshots are pending). */
  coverNode?: ReactNode;
  coverWidth?: number;
  coverHeight?: number;
  coverClassName?: string;
  /** Extra preview pictures shown alongside the cover, so visitors see everything they'll get. */
  extraImages?: LeadMagnetImage[];
  logo?: string;
  logoAlt?: string;
  logoLabel?: string;
  ctaLabel?: string;
  /** Search-result title and description (defaults: headline / subtitle). */
  seoTitle?: string;
  seoDescription?: string;
};

const FN = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
const KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const TRACK_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "fbclid", "gclid", "ttclid", "li_uuid"];

/** US phone: 10 digits (optional leading 1) → E.164. */
export const toUsE164 = (raw: string): string | null => {
  const d = raw.replace(/\D/g, "");
  const ten = d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
  return /^[2-9]\d{2}[2-9]\d{6}$/.test(ten) ? `+1${ten}` : null;
};

export const formatUsPhone = (raw: string): string => {
  let d = raw.replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) d = d.slice(1);
  d = d.slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

/** Lead fields for the existing qualification + Sheets pipeline, using only the current URL's params. */
export const isValidEmail = (raw: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(raw.trim()) && raw.trim().length <= 255;

export const buildLeadMagnetFields = (
  tag: string, name: string, company: string, phone: string, search: string, href: string, email = "",
) => {
  const q = new URLSearchParams(search);
  const fields: Record<string, string> = {
    full_name: name.trim(), company: company.trim(), phone,
    ...(email ? { email: email.trim().toLowerCase() } : {}),
    growth_blockers: "", ad_spend_12m: "", cta: tag, source: tag,
    landing_url: href, form_url: href,
  };
  for (const k of TRACK_KEYS) { const v = q.get(k); if (v) fields[k] = v.slice(0, 500); }
  return fields;
};

export const postFn = (fn: string, body: unknown) => fetch(`${FN}/${fn}`, {
  method: "POST",
  headers: { "Content-Type": "application/json", apikey: KEY, Authorization: `Bearer ${KEY}` },
  body: JSON.stringify(body),
});

const inputCls = "w-full rounded-lg border border-[#03214B]/20 bg-white px-4 py-3 text-base text-[#03214B] placeholder:text-[#03214B]/40 focus:border-[#FFB002] focus:outline-none focus:ring-2 focus:ring-[#FFB002]/40";

const LeadMagnetPage = ({ c }: { c: LeadMagnetConfig }) => {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !company.trim()) return setError("Please fill in your name and company.");
    const e164 = toUsE164(phone);
    if (!e164) return setError("Please enter a valid US phone number.");
    if (!isValidEmail(email)) return setError("Please enter a valid email address.");
    setSending(true);
    const session_id = crypto.randomUUID();
    const fields = buildLeadMagnetFields(c.tag, name, company, e164, window.location.search, window.location.href, email);
    try {
      const res = await postFn("qualification-submit", {
        session_id, fields,
        lead_magnet: c.leadMagnet,
      });
      if (!res.ok) throw new Error();
      postFn("vsl-lead-sync", { session_id, fields: { ...fields, status: "qualified", last_step: c.tag } }).catch(() => {});
      const w = window as unknown as { dataLayer?: unknown[] };
      (w.dataLayer = w.dataLayer || []).push({ event: "lm_submit", lead_magnet: c.tag });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const vslHref = `/vsl${typeof window !== "undefined" ? window.location.search : ""}`;
  const gallery: LeadMagnetImage[] = [
    ...(c.cover ? [{ src: c.cover, alt: c.coverAlt ?? c.headline, width: c.coverWidth, height: c.coverHeight }] : []),
    ...(c.extraImages ?? []),
  ];

  return (
    <main className="min-h-screen bg-[#03214B] px-5 py-6 text-white md:py-12">
      <Helmet>
        <title>{`${c.seoTitle ?? c.headline} | The Alpha Omega Hub`}</title>
        <meta name="description" content={c.seoDescription ?? c.subtitle} />
        <link rel="canonical" href={`https://thealphaomegahub.com${c.path}`} />
      </Helmet>
      <div className="mx-auto max-w-5xl">
        <img src={logoFull} alt="The Alpha Omega Hub" className="mx-auto h-10 w-auto md:h-12" />

        {done ? (
          <section className="mx-auto mt-14 max-w-xl rounded-2xl bg-white p-8 text-center text-[#03214B] shadow-xl md:p-10">
            <h1 className="font-display text-2xl font-bold md:text-3xl">
              You're all set! You'll receive it by text message at {formatUsPhone(phone)} and by email at {email.trim().toLowerCase()}.
            </h1>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button type="button" onClick={() => { setDone(false); }}
                className="rounded-lg border-2 border-[#03214B] px-5 py-3 font-semibold text-[#03214B] transition hover:bg-[#03214B]/5">
                Change phone number
              </button>
              <a href={vslHref} className="rounded-lg bg-[#FFB002] px-5 py-3 font-bold text-[#03214B] transition hover:brightness-105">
                Click here to view our services
              </a>
            </div>
          </section>
        ) : (
          <>
            <header className="mt-4 text-center md:mt-14">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFB002] md:text-sm">{c.eyebrow}</p>
              <h1 className="mx-auto mt-3 max-w-3xl font-display text-3xl font-bold leading-tight md:text-5xl">{c.headline}</h1>
              <p className="mx-auto mt-4 max-w-2xl text-base text-white/80 md:text-lg">{c.subtitle}</p>
            </header>
            <div className="mt-6 grid items-center gap-6 md:mt-10 md:grid-cols-2 md:items-start md:gap-12">
              <div className="w-full">
                {c.coverNode ? (
                  c.coverNode
                ) : (
                  <>
                    <div className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1 [scrollbar-width:none] md:hidden [&::-webkit-scrollbar]:hidden">
                      {gallery.map((img, i) => (
                        <img key={img.src} src={img.src} alt={img.alt}
                          width={img.width ?? c.coverWidth ?? 1199} height={img.height ?? c.coverHeight ?? 1312}
                          loading={i === 0 ? "eager" : "lazy"}
                          className="h-[108px] w-auto shrink-0 snap-center rounded-xl border border-white/10 shadow-[0_20px_45px_-18px_rgba(0,0,0,0.85)]" />
                      ))}
                    </div>
                    {gallery.length > 1 && (
                      <p className="mt-1.5 text-center text-[10px] font-semibold uppercase tracking-[0.16em] text-white/45 md:hidden">
                        Swipe to see all {gallery.length}
                      </p>
                    )}
                    <div className="hidden md:block">
                      <img src={c.cover} alt={c.coverAlt ?? c.headline} width={c.coverWidth ?? 1199} height={c.coverHeight ?? 1312}
                        className={c.coverClassName ?? "mx-auto w-full max-w-sm rounded-2xl md:max-w-md"} />
                      {(c.extraImages ?? []).length > 0 && (
                        <div className="mt-3 grid grid-cols-2 items-start gap-3">
                          {(c.extraImages ?? []).map((img) => (
                            <img key={img.src} src={img.src} alt={img.alt}
                              width={img.width ?? 1199} height={img.height ?? 1312}
                              className="h-auto w-full rounded-xl border border-white/10 shadow-[0_20px_45px_-18px_rgba(0,0,0,0.85)]" />
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
                {c.logo && (
                  <div className="mt-3 hidden justify-center md:flex md:justify-start">
                    <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 ring-1 ring-white/15">
                      <img src={c.logo} alt={c.logoAlt ?? ""} className="h-4 w-auto" />
                      <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/70">
                        {c.logoLabel ?? "Google Doc"}
                      </span>
                    </span>
                  </div>
                )}
              </div>
              <form onSubmit={onSubmit} noValidate className="w-full rounded-2xl bg-white p-6 text-[#03214B] shadow-xl md:p-8">
                <div className="space-y-3 md:space-y-4">
                  <label className="block">
                    <span className="mb-1 block text-sm font-semibold">Full name</span>
                    <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required maxLength={120} />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-sm font-semibold">Company name</span>
                    <input className={inputCls} value={company} autoComplete="organization" required maxLength={120}
                      onChange={(e) => setCompany(e.target.value)} />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-sm font-semibold">Phone number</span>
                    <input className={inputCls} type="tel" inputMode="tel" autoComplete="tel" placeholder="(555) 555-5555"
                      value={phone} onChange={(e) => setPhone(formatUsPhone(e.target.value))} required />
                    <span className="mt-1 block text-xs text-[#03214B]/60">We'll text you the resource.</span>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-sm font-semibold">Email</span>
                    <input className={inputCls} type="email" inputMode="email" autoComplete="email" placeholder="you@company.com"
                      value={email} onChange={(e) => setEmail(e.target.value)} required maxLength={255} />
                    <span className="mt-1 block text-xs text-[#03214B]/60">We'll also email it to you.</span>
                  </label>
                </div>
                {error && <p className="mt-4 text-sm font-medium text-red-600" role="alert">{error}</p>}
                <button type="submit" disabled={sending}
                  className="mt-5 w-full rounded-lg bg-[#FFB002] px-6 py-4 text-lg font-bold text-[#03214B] transition hover:brightness-105 disabled:opacity-60 md:mt-6">
                  {sending ? "Sending…" : (c.ctaLabel ?? "Get access now")}
                </button>
                {c.footerLink && (
                  <a href={c.footerLink.href} className="mt-4 block text-center text-sm font-semibold text-[#03214B]/70 underline-offset-4 hover:text-[#03214B] hover:underline">
                    {c.footerLink.label}
                  </a>
                )}
              </form>
            </div>
          </>
        )}
      </div>
    </main>
  );
};

export default LeadMagnetPage;
